import { useState, useCallback } from 'react';
import defaultTechnicalManualContent from './technicalManualContent';

const STORAGE_KEY = 'sk_tech_manual_content';
const IMAGE_STORAGE_KEY = 'sk_tech_manual_images';

/**
 * Deep-clones an object via JSON serialization.
 */
const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

/**
 * Loads persisted technical manual content from localStorage.
 * Falls back to defaultTechnicalManualContent if nothing is stored
 * or if the stored version is older than the shipped default.
 */
const loadContent = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // If stored content is from an older version, discard it
      if (parsed._contentVersion && parsed._contentVersion >= defaultTechnicalManualContent._contentVersion) {
        return parsed;
      }
      // Outdated — clear and use new default
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(IMAGE_STORAGE_KEY);
    }
  } catch (e) {
    console.warn('Failed to load technical manual from localStorage:', e);
  }
  return deepClone(defaultTechnicalManualContent);
};

/**
 * Loads persisted custom image overrides (base64 data URLs) from localStorage.
 * Key: section id (e.g. "ch1-s1"), Value: { src: dataURL, caption: string }
 */
const loadImageOverrides = () => {
  try {
    const stored = localStorage.getItem(IMAGE_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) {
    console.warn('Failed to load technical manual images from localStorage:', e);
  }
  return {};
};

/**
 * useTechManualStore — central state hook for the editable technical manual.
 *
 * Returns:
 *   content          — current manual content (meta + chapters[])
 *   imageOverrides   — { [sectionId]: { src, caption } } for user-uploaded images
 *   updateMeta       — (field, value) => void
 *   updateSection    — (chapterId, sectionId, field, value) => void
 *   updateStepItem   — (chapterId, sectionId, index, value) => void
 *   addStep          — (chapterId, sectionId) => void
 *   removeStep       — (chapterId, sectionId, index) => void
 *   moveStep         — (chapterId, sectionId, fromIndex, toIndex) => void
 *   updateImage      — (sectionId, src, caption) => void — src is base64 or URL
 *   resetChapter     — (chapterId) => void — restores chapter to default
 *   resetAll         — () => void — restores entire manual to default
 *   saveNow          — () => void — force persist to localStorage
 *   isDirty          — boolean — true if content differs from default
 */
const useTechManualStore = () => {
  const [content, setContent] = useState(loadContent);
  const [imageOverrides, setImageOverrides] = useState(loadImageOverrides);

  const persist = useCallback((newContent, newImages) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newContent));
      if (newImages !== undefined) {
        localStorage.setItem(IMAGE_STORAGE_KEY, JSON.stringify(newImages));
      }
    } catch (e) {
      console.warn('Failed to persist technical manual:', e);
    }
  }, []);

  const updateMeta = useCallback((field, value) => {
    setContent((prev) => {
      const next = { ...prev, meta: { ...prev.meta, [field]: value } };
      persist(next);
      return next;
    });
  }, [persist]);

  const updateSection = useCallback((chapterId, sectionId, field, value) => {
    setContent((prev) => {
      const next = deepClone(prev);
      const ch = next.chapters.find((c) => c.id === chapterId);
      if (!ch) return prev;
      const sec = ch.sections.find((s) => s.id === sectionId);
      if (!sec) return prev;
      sec[field] = value;
      persist(next);
      return next;
    });
  }, [persist]);

  const updateStepItem = useCallback((chapterId, sectionId, index, value) => {
    setContent((prev) => {
      const next = deepClone(prev);
      const ch = next.chapters.find((c) => c.id === chapterId);
      if (!ch) return prev;
      const sec = ch.sections.find((s) => s.id === sectionId);
      if (!sec) return prev;
      const arr = sec.steps || sec.items || [];
      arr[index] = value;
      persist(next);
      return next;
    });
  }, [persist]);

  const addStep = useCallback((chapterId, sectionId) => {
    setContent((prev) => {
      const next = deepClone(prev);
      const ch = next.chapters.find((c) => c.id === chapterId);
      if (!ch) return prev;
      const sec = ch.sections.find((s) => s.id === sectionId);
      if (!sec) return prev;
      if (sec.steps) sec.steps.push('');
      else if (sec.items) sec.items.push('');
      persist(next);
      return next;
    });
  }, [persist]);

  const removeStep = useCallback((chapterId, sectionId, index) => {
    setContent((prev) => {
      const next = deepClone(prev);
      const ch = next.chapters.find((c) => c.id === chapterId);
      if (!ch) return prev;
      const sec = ch.sections.find((s) => s.id === sectionId);
      if (!sec) return prev;
      if (sec.steps) sec.steps.splice(index, 1);
      else if (sec.items) sec.items.splice(index, 1);
      persist(next);
      return next;
    });
  }, [persist]);

  const moveStep = useCallback((chapterId, sectionId, fromIndex, toIndex) => {
    setContent((prev) => {
      const next = deepClone(prev);
      const ch = next.chapters.find((c) => c.id === chapterId);
      if (!ch) return prev;
      const sec = ch.sections.find((s) => s.id === sectionId);
      if (!sec) return prev;
      const arr = sec.steps || sec.items;
      if (!arr) return prev;
      const [item] = arr.splice(fromIndex, 1);
      arr.splice(toIndex, 0, item);
      persist(next);
      return next;
    });
  }, [persist]);

  const updateImage = useCallback((sectionId, src, caption) => {
    setImageOverrides((prev) => {
      const next = { ...prev, [sectionId]: { src, caption } };
      localStorage.setItem(IMAGE_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const removeImageOverride = useCallback((sectionId) => {
    setImageOverrides((prev) => {
      const next = { ...prev };
      delete next[sectionId];
      localStorage.setItem(IMAGE_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const resetChapter = useCallback((chapterId) => {
    const defaultChapter = defaultTechnicalManualContent.chapters.find((c) => c.id === chapterId);
    if (!defaultChapter) return;
    setContent((prev) => {
      const next = deepClone(prev);
      const idx = next.chapters.findIndex((c) => c.id === chapterId);
      if (idx === -1) return prev;
      next.chapters[idx] = deepClone(defaultChapter);
      persist(next);
      return next;
    });
    // Also remove image overrides for this chapter's sections
    setImageOverrides((prev) => {
      const next = { ...prev };
      defaultChapter.sections.forEach((s) => delete next[s.id]);
      localStorage.setItem(IMAGE_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, [persist]);

  const resetAll = useCallback(() => {
    const fresh = deepClone(defaultTechnicalManualContent);
    setContent(fresh);
    setImageOverrides({});
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(IMAGE_STORAGE_KEY);
  }, []);

  const saveNow = useCallback(() => {
    persist(content, imageOverrides);
  }, [content, imageOverrides, persist]);

  const replaceContent = useCallback((newContent) => {
    setContent(newContent);
    persist(newContent, imageOverrides);
  }, [imageOverrides, persist]);

  return {
    content,
    imageOverrides,
    replaceContent,
    updateMeta,
    updateSection,
    updateStepItem,
    addStep,
    removeStep,
    moveStep,
    updateImage,
    removeImageOverride,
    resetChapter,
    resetAll,
    saveNow,
  };
};

export default useTechManualStore;
